import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getGuestToken } from "@/lib/guest";
import ZButton from "@/components/ZButton";
import { Star } from "lucide-react";

function StarsInput({ value, onChange, testId }) {
  return (
    <div style={{ display: "flex", gap: 6 }} data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${testId}-${n}`} style={{ background: "transparent", border: 0, cursor: "pointer", padding: 4 }}>
          <Star size={28} fill={n <= value ? "var(--z-orange)" : "none"} color={n <= value ? "var(--z-orange)" : "var(--z-border)"} />
        </button>
      ))}
    </div>
  );
}

export default function SurveyPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/surveys/${id}`).then((r) => setS(r.data)).finally(() => setLoading(false));
  }, [id]);

  const setAnswer = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

  const submit = async () => {
    setErr("");
    const out = s.questions.map((q) => {
      const v = answers[q.id];
      const a = { question_id: q.id };
      if (q.type === "text") a.text = v || "";
      else if (q.type === "star") a.rating = v || 0;
      else if (q.type === "single") a.option_ids = v ? [v] : [];
      else if (q.type === "multi") a.option_ids = v || [];
      return a;
    });
    setSubmitting(true);
    try {
      const payload = { answers: out };
      if (!user) payload.respondent_token = getGuestToken();
      await api.post(`/surveys/${id}/respond`, payload);
      setDone(true);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  if (!s) return <div className="z-container">Sondage introuvable.</div>;

  if (done) {
    return (
      <div className="z-container" style={{ maxWidth: 460, textAlign: "center", padding: "60px 20px" }} data-testid="survey-thanks">
        <div style={{ fontSize: 50, marginBottom: 16 }}>🙏</div>
        <h2>Merci pour votre retour !</h2>
        <p style={{ color: "var(--z-muted)" }}>Votre réponse a bien été enregistrée.</p>
      </div>
    );
  }

  return (
    <div className="z-container" style={{ maxWidth: 600 }} data-testid="survey-page">
      <h1 className="z-page-title" data-testid="survey-title">{s.title}</h1>
      {s.description && <p className="z-page-sub">{s.description}</p>}

      <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
        {s.questions.map((q, i) => (
          <div key={q.id} className="z-card" style={{ padding: 18, marginBottom: 14 }} data-testid={`survey-q-${q.id}`}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>
              {i + 1}. {q.text}
            </div>
            {q.type === "text" && (
              <textarea className="z-textarea" value={answers[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)} data-testid={`survey-input-text-${q.id}`} />
            )}
            {q.type === "star" && (
              <StarsInput value={answers[q.id] || 0} onChange={(v) => setAnswer(q.id, v)} testId={`survey-input-star-${q.id}`} />
            )}
            {q.type === "single" && (
              <div style={{ display: "grid", gap: 8 }}>
                {q.options.map((o) => (
                  <button
                    key={o.id} type="button" onClick={() => setAnswer(q.id, o.id)}
                    data-testid={`survey-option-${q.id}-${o.id}`}
                    className="z-chip" style={{ justifyContent: "flex-start", padding: "12px 14px", ...(answers[q.id] === o.id ? { background: "var(--z-teal)", color: "white", borderColor: "var(--z-teal)" } : {}) }}
                  >{o.label}</button>
                ))}
              </div>
            )}
            {q.type === "multi" && (
              <div style={{ display: "grid", gap: 8 }}>
                {q.options.map((o) => {
                  const arr = answers[q.id] || [];
                  const active = arr.includes(o.id);
                  return (
                    <button
                      key={o.id} type="button"
                      onClick={() => setAnswer(q.id, active ? arr.filter((x) => x !== o.id) : [...arr, o.id])}
                      data-testid={`survey-option-${q.id}-${o.id}`}
                      className="z-chip" style={{ justifyContent: "flex-start", padding: "12px 14px", ...(active ? { background: "var(--z-teal)", color: "white", borderColor: "var(--z-teal)" } : {}) }}
                    >{o.label}</button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        {err && <p className="z-error-text" data-testid="survey-error">{err}</p>}
        <ZButton type="submit" block loading={submitting} data-testid="survey-submit-btn">Envoyer mes réponses</ZButton>
      </form>
    </div>
  );
}
